import type { RootState } from "~/redux/store";
import type { AnyAction } from "~/redux/types";
import type { AddToCartFormData } from "~/AddToCart/hooks/useAddToCartNew";
import { userIsCustomer } from "~/utils/roles";
import { ProductService } from "~/AddToCart/ProductService";
import {
  getNetPrice,
  getPackagingInformationByCode,
  getSurcharge,
} from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { selectProperAmountInCart } from "~/AddToCart/redux/cartAmounts/cartAmountSelectors";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { bulkAddToCartApi } from "~/AddToCart/redux/addToCart/actions";
import type { ThunkAction } from "redux-thunk";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import type { PurchasePlace, PurchaseType } from "~/feature/Analytics/ga/recommendations/types";

interface AddToCartB2CDetails {
  productCode: string;
  quantity: number;
  comment?: string;
}

interface AddToCartB2BDetails {
  productCode: string;
  packCode: string;
  quantity: number;
  priceReference: string;
  saleEnabled?: boolean;
  comment?: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface AddToCartSalesmanDetails extends AddToCartB2BDetails {
  // TODO
}

type AddToCartDetails = AddToCartB2CDetails | AddToCartB2BDetails | AddToCartSalesmanDetails;

export function addToCartThunk(
  data: AddToCartDetails | AddToCartDetails[],
  restrictionQuery?: string,
  context?: {
    page: PurchasePlace;
    recommendationPlace?: PurchasePlace;
    recommendationType?: PurchaseType;
  },
): ThunkAction<void, RootState, undefined, AnyAction> {
  return async (dispatch, getState) => {
    const state = getState();
    const items = Array.isArray(data) ? data : [data];

    const isRecommendation = !!context?.recommendationType;
    const apiContext = isRecommendation
      ? {
          recommendationType: context.recommendationType,
          recommendationPlace: context.recommendationPlace,
          page: context.page,
        }
      : undefined;

    let apiItems: (AddToCartFormData & {
      productCode: string;
    })[];
    if (userIsCustomer()) {
      apiItems = await Promise.all(
        items.map((item) => {
          const { packCode, priceReference, productCode, quantity, saleEnabled, comment } = item as AddToCartB2BDetails;
          return ProductService.getProductDescriptor(productCode, priceReference, {
            fetch: true,
          }).promise.then((productDescriptor: ProductDescriptor) => {
            const packageInfo = getPackagingInformationByCode(productDescriptor, packCode);

            const totalQuantity =
              selectProperAmountInCart({
                productCode,
                packCode,
                priceReference,
              })(state) + quantity;

            const price = getNetPrice({ product: productDescriptor, packCode, totalQuantity });
            const surcharge = getSurcharge({ product: productDescriptor, packCode, totalQuantity });

            return {
              productCode: productDescriptor.base.hybrisCode,
              selectedPackCode: packCode,
              selectedPack: packageInfo,
              quantity: quantity,
              comment: comment ?? "",
              netPrice: price,
              priceUnit: price.unit ?? 1,
              priceReference: priceReference,
              totalQuantity,
              optionalParts: {},
              surcharge,
              restrictionQuery: restrictionQuery ?? "",
              showSuccessDrawer: true,
              salePriceEnabled: saleEnabled ?? false,
            };
          });
        }),
      );
    } else {
      apiItems = items.map((data) => ({
        productCode: data.productCode,
        quantity: data.quantity,
        restrictionQuery: restrictionQuery ?? "",
        netPrice: null,
        comment: data.comment,
        optionalParts: {},
        priceReference: getInitialPriceReference(),
        selectedPackCode: INVALID_PACKAGE_CODE,
        selectedPack: null,
        priceUnit: 1,
        salePriceEnabled: false,
        surcharge: 0,
        totalQuantity: 1,
      }));
    }
    bulkAddToCartApi({ items: apiItems, restrictionQuery: restrictionQuery ?? "", context: apiContext });
  };
}
